import { getLocale } from '@/i18n/locale';
import { isResortWithoutWeSki } from '@/util/isResortWithoutWeSki';
import { isRegionWithoutWeSki } from '@/util/isRegionWithoutWeSki';
import { isSkiRentalsLocale } from '@/util/i18nFlags';
import { isSouthernHemisphereCountry } from '@/i18n/country';
import { PlaningViewsEnum } from './constants';

export interface RentalInput {
  hasSkiRental: boolean;
  isPartner: boolean;
  initialTab?: PlaningViewsEnum;
  redirectSkiRentalPath?: string;
  rentalUrl?: string;
  resortCountry?: string;
}

export interface RentalClassification {
  /** Rental shows as a clickable in-page tab (rental form or partner panel). */
  asTab: boolean;
  /** Rental shows as an external/internal link chip rather than a panel. */
  asLink: boolean;
  /** The link points to an external partner URL (drives analytics). */
  isExternalUrl: boolean;
  /** Whether a rental tab/link is eligible to show at all. */
  showTab: boolean;
  /** Resolved URL for the rental link, when shown as a link. */
  url?: string;
}

/**
 * Classifies how the rental affordance should render. Extracted from
 * TripControlBlock so the control block (tab/link rendering) and PlanYourTrip
 * (emptiness + default view) share one source of truth.
 *
 * `asTab` and `asLink` partition the rental space: exactly one is true.
 */
export const classifyRental = ({
  hasSkiRental,
  isPartner,
  initialTab,
  redirectSkiRentalPath,
  rentalUrl,
  resortCountry,
}: RentalInput): RentalClassification => {
  const isSkiRentalAPage = hasSkiRental && isSkiRentalsLocale();
  const isEUresort = !isSouthernHemisphereCountry(resortCountry);

  const isExternalUrl = !isSkiRentalAPage && isPartner;
  const isForm = isSkiRentalAPage && initialTab === PlaningViewsEnum.rental;
  const isInternalUrl = isSkiRentalAPage
    && initialTab !== PlaningViewsEnum.rental
    && !!redirectSkiRentalPath;
  const isPlainTab = (isSkiRentalAPage && initialTab !== PlaningViewsEnum.rental && !redirectSkiRentalPath)
    || (!isSkiRentalAPage && !isPartner);

  return {
    asTab: isForm || isPlainTab,
    asLink: isExternalUrl || isInternalUrl,
    isExternalUrl,
    showTab: !!((((!isEUresort && isPartner) || isEUresort) && rentalUrl) || isSkiRentalAPage),
    url: isInternalUrl ? redirectSkiRentalPath : rentalUrl,
  };
};

export interface PanelsInput extends RentalInput {
  lodgingProvider?: 'weski';
  weSkiDestinationId?: string | null;
  regionId?: string;
  resortId?: string;
  templateName?: string;
}

export interface AvailablePanels {
  lodging: boolean;
  rental: boolean;
}

/**
 * Whether the rental panel is actually reachable (not merely a link).
 * Mirrors TripControlBlock's `showRentalAsTab`, minus the noHeader gate —
 * panels render regardless of noHeader; noHeader only hides the tab chrome.
 */
const isRentalPanelAvailable = (input: PanelsInput): boolean => {
  if (input.initialTab === PlaningViewsEnum.rental) {
    return true;
  }
  const rental = classifyRental(input);
  const showMoreThanOneTab = !(input.templateName === 'Region Lodging' || input.templateName === 'Resort Lodging');
  return rental.showTab && rental.asTab && showMoreThanOneTab;
};

/**
 * The in-page panels this widget can actually surface. External link/CTA
 * chips (lifts, lessons, external rental link) are intentionally excluded —
 * only panels justify rendering the widget.
 */
export const getAvailablePanels = (input: PanelsInput): AvailablePanels => {
  const locale = getLocale();

  const lodging = input.lodgingProvider === 'weski'
    && !!input.weSkiDestinationId
    && !isRegionWithoutWeSki(input.regionId?.toString() || '', locale)
    && !isResortWithoutWeSki(input.resortId?.toString() || '', locale);

  return {
    lodging,
    rental: isRentalPanelAvailable(input),
  };
};

/**
 * The view the widget should start on so an available panel is visible.
 * Honors an explicit initialTab, then prefers lodging, then rental. Falls
 * back to lodging (the caller returns null when no panel is available).
 */
export const getDefaultView = (
  panels: AvailablePanels,
  initialTab?: PlaningViewsEnum,
): PlaningViewsEnum => {
  if (initialTab) {
    return initialTab;
  }
  if (panels.lodging) {
    return PlaningViewsEnum.lodging;
  }
  if (panels.rental) {
    return PlaningViewsEnum.rental;
  }
  return PlaningViewsEnum.lodging;
};
